/**
 * CookiePolicy - Cookie Policy page (/legal/cookies)
 *
 * v2.0 - 2026-03-23 - Refactored to use LegalPageShell; legal@ email; removed "Last updated" line
 * v1.0 - 2026-03-17 - GDPR-03: Cookie Policy page
 */
import React from 'react';
import { Box, Container, Divider, Link, Typography } from '@mui/material';
import LegalPageShell, { LEGAL_STICKY_BAR_HEIGHT, legalBodyTextSx } from '../../components/LegalPageShell';

// ─── Section registry ─────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'intro',     label: 'What are cookies?' },
  { id: 'essential', label: 'Essential cookies' },
  { id: 'analytics', label: 'Analytics cookies' },
  { id: 'manage',    label: 'How to manage cookies' },
  { id: 'contact',   label: 'Questions?' },
];

const CookiePolicy: React.FC = () => (
  <LegalPageShell title="Cookie Policy" lastUpdated="March 23, 2026" sections={SECTIONS}>
    <Container maxWidth="md" sx={{ py: 6 }}>

      {/* Intro */}
      <Box id="intro" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="body1" sx={{ ...legalBodyTextSx, mb: 4, lineHeight: 1.75 }}>
          Cookies are small text files that a website saves to your browser. They help the site
          remember things like whether you're logged in so you don't have to sign in every time.
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Essential cookies */}
      <Box id="essential" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          Essential cookies: always active
        </Typography>
        <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 3 }}>
          These cookies are required for MyLojic to work. You cannot turn them off.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 5 }}>
          {/* Clerk session */}
          <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
              Session token
            </Typography>
            <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 0.5 }}>
              <strong>Purpose:</strong> Keeps you logged in while you're using MyLojic. Without it
              you would need to sign in on every page.
            </Typography>
            <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 0.5 }}>
              <strong>Set by:</strong> Clerk (our authentication provider)
            </Typography>
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Duration:</strong> Until you sign out or your session expires (typically a few
              days to a few weeks depending on your device).
            </Typography>
          </Box>

          {/* Consent record */}
          <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
              Cookie consent record
            </Typography>
            <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 0.5 }}>
              <strong>Purpose:</strong> Remembers whether you have accepted or declined optional
              cookies so we don't show you the consent banner on every visit.
            </Typography>
            <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 0.5 }}>
              <strong>Set by:</strong> MyLojic (stored in your browser's local storage)
            </Typography>
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Duration:</strong> 1 year. After that we will ask for your preference again.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Analytics cookies */}
      <Box id="analytics" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          Analytics cookies: optional
        </Typography>
        <Typography variant="body2" sx={{ ...legalBodyTextSx, mb: 3 }}>
          These cookies would help us understand how visitors use the site so we can improve it.
          You can turn them on or off using the cookie banner at the bottom of any page.
        </Typography>

        <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'divider', mb: 5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            We do not currently use any analytics cookies. This section will be updated if we add
            analytics tools in the future.
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Managing cookies */}
      <Box id="manage" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          How to manage cookies
        </Typography>
        <Typography variant="body1" sx={{ ...legalBodyTextSx, mb: 2, lineHeight: 1.75 }}>
          You can control cookies through the cookie banner at the bottom of the page. You can also
          manage or delete cookies directly in your browser settings:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 5 }}>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Firefox:</strong> Settings → Privacy &amp; Security → Cookies and Site Data
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Safari:</strong> Preferences → Privacy → Manage Website Data
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2" sx={legalBodyTextSx}>
              <strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Contact */}
      <Box id="contact" sx={{ scrollMarginTop: LEGAL_STICKY_BAR_HEIGHT + 16 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
          Questions?
        </Typography>
        <Typography variant="body1" sx={{ ...legalBodyTextSx, lineHeight: 1.75 }}>
          If you have any questions about how we use cookies, email us at{' '}
          <Link href="mailto:legal@mylojic.com" sx={{ color: 'primary.main' }}>
            legal@mylojic.com
          </Link>
          .
        </Typography>
      </Box>

    </Container>
  </LegalPageShell>
);

export default CookiePolicy;
